import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import VideoUpload from '../VideoUpload/VideoUpload';
import ThumbnailField from '../ImageUpload/ThumbnailField';
import HeadlineField from '../HeadlineField/HeadlineField';
import DescriptionField from '../DescriptionField/DescriptionField';
import BrandLogoField from '../ImageUpload/BrandLogoField';
import materialTheme from '../../reactMaterialTheme';
import Wysiwyg from '../../shared/Wysiwyg';
import { useCreativeInput } from '../../hooks';
import { PartnerProvider } from '../../partners/partner';
import './NativeOutstreamBuilder.scss';
import CaptionsField from "../ImageUpload/CaptionsField";
import Alert from "../../../react/components/base/alert/Alert";

export const NativeOutstreamBuilder = props => {
  return (
    <PartnerProvider>
      <NativeOutstreamForm {...props} />
    </PartnerProvider>
  );
};

const defaultCreativeData = () => ({
  headline: 'Watch this video for more information',
  description: 'Like this video? Click here to learn more.',
  advertiser: 'Sponsor',
  brandLogoUrl: '',
  thumbnailUrl: 'https://legacy-design-assets.s3.amazonaws.com/images/click-for-more.png',
  customEngagementLabel: 'Learn More'
});

const VideoInput = props => {
  const [activeTabIndex, setActiveTabIndex] = React.useState(
    props.creative.mediaUrl ? 1 : 0
  );

  React.useEffect(
    function notifyVideoTypeChange() {
      props.onVideoTypeChange(activeTabIndex === 0 ? 'vast' : 'mp4');
    },
    [activeTabIndex]
  );

  return (
    <Tabs
      selectedIndex={activeTabIndex}
      onSelect={tabIndex => setActiveTabIndex(tabIndex)}
    >
      <TabList>
        <Tab>Use Vast/VPAID</Tab>
        <Tab>Upload MP4 video</Tab>
      </TabList>

      <TabPanel>
        <TextField
          {...props.vastTagProps}
          label="VAST/VPAID XML will override uploaded videos."
          multiline
          rows={1}
          fullWidth
          inputProps={{ 'data-testid': 'creative-vast-tag-input' }}
          onFocus={event => event.target.select()}
        />
      </TabPanel>
      <TabPanel>
        <VideoUpload
          SaveCreativeService={props.SaveCreativeService}
          CreativeService={props.CreativeService}
        />
      </TabPanel>
    </Tabs>
  );
};

VideoInput.propTypes = {
  creative: PropTypes.object.isRequired,
  onVideoTypeChange: PropTypes.func.isRequired,
  CreativeService: PropTypes.shape({
    set: PropTypes.func.isRequired
  }).isRequired,
  SaveCreativeService: PropTypes.shape({
    save: PropTypes.func.isRequired
  }).isRequired,
  vastTagProps: PropTypes.object.isRequired
};

// Can be renamed to NativeOutstreamBuilder once PartnerProvider is placed upstream
const NativeOutstreamForm = props => {
  const defaultFields = props.creative.id ? {} : defaultCreativeData();

  const [creative, setCreative] = useState({
    ...defaultFields,
    ...props.creative
  });

  const [showButtonInputs, setShowButtonInputs] = useState(false);

  const creativeInputProps = useCreativeInput(
    [
      'name',
      'advertiser',
      'brandLogoUrl',
      'vastTag',
      'headline',
      'description',
      'customEngagementLabel',
      'customEngagementUrl',
      'thumbnailUrl',
      'isiAutoscroll',
      'isiText',
      'srtFileUrl'
    ],
    creative,
    setCreative,
    props.CreativeService
  );

  const onThumbnailUrlChange = url => {
    props.CreativeService.set('thumbnailUrl', url);
    setCreative({
      ...creative,
      thumbnailUrl: url
    });
  };

  const onSrtFileUrlChange = url => {
    props.CreativeService.set('srtFileUrl', url);
    setCreative({
      ...creative,
      srtFileUrl: url
    });
  };

  const handleVideoTypeChange = videoType => {
    props.onVideoTypeChange(videoType);
    setShowButtonInputs(videoType === 'mp4');
  };

  const handleFocus = event => event.target.select();

  return (
    <MuiThemeProvider theme={materialTheme}>
      <form className="p2 native-outstream-builder">
        <div className="row">
          <TextField
            {...creativeInputProps.nameProps}
            label="Ad Name"
            className="col-xs-10"
            name="name"
            inputProps={{ 'data-testid': 'creative-name-input' }}
            onFocus={handleFocus}
            required
          />
        </div>
        <div className="row mt2">
          <TextField
            {...creativeInputProps.advertiserProps}
            label={
              <label htmlFor={creativeInputProps.advertiserProps.id}>
                Brand Name
              </label>
            }
            className="col-xs-10"
            onFocus={event => event.target.select()}
            required
          />
        </div>
        <div className="row mt2 flex">
          <BrandLogoField
            logoUrl={creativeInputProps.brandLogoUrlProps.value}
            onLogoUrlChange={creativeInputProps.brandLogoUrlProps.onChange}
          />
          <CaptionsField
            srtFileUrl={creative.srtFileUrl}
            onSrtFileUrlChange={onSrtFileUrlChange}
            disabled={!creative.vastTag}
          />
        </div>
        <h6>*Captions only available for VAST videos</h6>
        <div className="row mt2">
          <VideoInput
            creative={creative}
            vastTagProps={creativeInputProps.vastTagProps}
            onVideoTypeChange={handleVideoTypeChange}
            CreativeService={props.CreativeService}
            SaveCreativeService={props.SaveCreativeService}
          />
        </div>
        <div className="row native-outstream-builder_thumbnail-upload mt2">
          <ThumbnailField
            thumbnailUrl={creative.thumbnailUrl}
            onChange={onThumbnailUrlChange}
          />
          <div className="native-outstream-builder_text-fields ml2">
            {creative.srtFileUrl &&
            <Alert severity="info">Headline and description disabled when captions are present</Alert>
            }
            <HeadlineField
              headline={creative.srtFileUrl ? 'Click Here to Learn More' : creativeInputProps.headlineProps.value}
              onChange={creativeInputProps.headlineProps.onChange}
              disabled={!!creative.srtFileUrl}
            />
            <DescriptionField
              description={creative.srtFileUrl ? '' : creativeInputProps.descriptionProps.value}
              onChange={creativeInputProps.descriptionProps.onChange}
              disabled={!!creative.srtFileUrl}
            />
            {showButtonInputs ? (
              <>
                <TextField
                  {...creativeInputProps.customEngagementUrlProps}
                  label="Clickout URL"
                  className="col-xs-10"
                  inputProps={{
                    'data-testid': 'creative-custom-engagement-url-input'
                  }}
                  onFocus={handleFocus}
                />
                <TextField
                  {...creativeInputProps.customEngagementLabelProps}
                  label="Button Text (Optional)"
                  className="col-xs-10 mt2"
                  inputProps={{
                    'data-testid': 'creative-custom-engagement-label-input'
                  }}
                  onFocus={handleFocus}
                />
              </>
            ) : null}
          </div>
        </div>
        {creative.isiText === null || creative.isiText === undefined ? null : (
          <div className="row mt3">
            <div className="form-group inline">
              <h3 className="inline m0 vertical-align-middle">
                Important Safety Information
              </h3>
              <div className="vertical-align-middle pull-right">
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isiAutoscroll"
                      checked={creativeInputProps.isiAutoscrollProps.value}
                      onChange={creativeInputProps.isiAutoscrollProps.onChange}
                    />
                  }
                  label="Enable Auto-Scrolling"
                />
              </div>
              <Wysiwyg
                placeholder="Write your important safety information message"
                value={creative.isiText}
                onChange={creativeInputProps.isiTextProps.onChange}
              />
            </div>
          </div>
        )}
      </form>
    </MuiThemeProvider>
  );
};

NativeOutstreamBuilder.propTypes = {
  creative: PropTypes.shape({}).isRequired,
  CreativeService: PropTypes.shape({
    set: PropTypes.func.isRequired
  }).isRequired,
  SaveCreativeService: PropTypes.shape({
    save: PropTypes.func.isRequired
  }).isRequired,
  $modal: PropTypes.object.isRequired,
  $rootScope: PropTypes.object.isRequired,
  onVideoTypeChange: PropTypes.func.isRequired
};

NativeOutstreamForm.propTypes = NativeOutstreamBuilder.propTypes;
